import { useContext, useEffect, useState } from "react";
import Joi from "joi";
import { RenderInput } from "../../common/Elements";
import useToast from "hooks/useToast";
import { useTranslation } from "react-i18next";
import SelectInput from "components/SelectInput";
import ClassSchema from "./ClassSchema";
import Button from "components/Button";
import { DeskIcon } from "components/SVGs";
import { Typography } from "@mui/material";
import { gradesList, teachersList } from "services/Common";
import { MasterDataContext } from "contexts/MasterDataContext";
import {
  IClass,
  selectClassesActionMode,
  selectUpdateItem,
} from "features/Classes";
import { ActionType, IValidationErrors } from "features/common/Interfaces";
import {
  IAddClassRequest,
  IUpdateClassRequest,
  useAddClassMutation,
  useUpdateClassMutation,
} from "app/services/classes";
import { useAppSelector } from "app/hooks";
import { FireBaseContext } from "contexts/FireBase";

const customStyles = {
  root: {
    direction: "rtl",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "580px",
    padding: 0,
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "17px",
    overflowY: "auto",
    overflowX: "hidden",
    alignItems: "center",
    justifyContent: "center",
    "& > :not(:last-child)": {
      paddingBottom: "10px",
    },
  },
  form: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "350px",
  },
  name: {
    textAlign: "center",
    color: "rgb(76 76 76)",
    fontSize: "20px",
    fontWeight: "bold",
    paddingBottom: "20px",
  },
  label: {
    fontSize: "16px",
    color: "rgba(141 141 141)",
  },
  checkbox: {
    padding: "4px",
    "& svg": {
      fontSize: "18px",
    },
  },
};

interface IContentProps {
  onSubmit(): void;
}
function Content({ onSubmit }: IContentProps) {
  const { logEvent } = useContext(FireBaseContext);
  const [formData, setFormData] =
    useState<Pick<IClass, "name" | "gradeId" | "teacherId" | "id">>();
  const [grades, setGrades] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [errors, setErrors] = useState<IValidationErrors>({});
  const updateItem = useAppSelector((state) => selectUpdateItem(state));
  const actionMode = useAppSelector((state) => selectClassesActionMode(state));
  const {
    state: { schoolDetails },
  } = useContext(MasterDataContext);
  const [addClass, { isLoading: addIsloading }] = useAddClassMutation();
  const [updateClass, { isLoading: updateIsloading }] =
    useUpdateClassMutation();
  const toast = useToast();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (actionMode === ActionType.update && updateItem) {
      setFormData({
        name: updateItem.name,
        gradeId: updateItem.gradeId,
        teacherId: updateItem.teacherId,
        id: updateItem.id,
      });
    }
  }, []);

  const fetchGrades = async () => {
    try {
      const response = await gradesList();
      if (response?._statusCode === 200) {
        const list = response._entity?.map((item: any) => ({
          value: item._id,
          label: t(item._code),
        }));
        setGrades(list);
      } else throw response;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTeachers = async (schoolId: number) => {
    try {
      const response = await teachersList(schoolId);
      if (response?._statusCode === 200) {
        const list = response._entity?.map((item: any) => ({
          value: item._teacherId,
          label: (
            <span
              style={{
                display: "block",
                whiteSpace: "nowrap",
              }}
            >
              {`${item?._name ?? ""} ${item?._lastName ?? ""}`}
            </span>
          ),
        }));
        setTeachers(list);
      } else throw response;
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchGrades();
    if (schoolDetails?.schoolId) fetchTeachers(schoolDetails?.schoolId);
  }, [schoolDetails?.schoolId]);

  const gradeChangeHandler = (event: any) => {
    setFormData({ ...formData, gradeId: event.currentTarget.value } as Pick<
      IClass,
      "name" | "gradeId" | "teacherId" | "id"
    >);
  };
  const teacherChangeHandler = (event: any) => {
    setFormData({
      ...formData,
      teacherId: event.currentTarget.value,
    } as Pick<IClass, "name" | "gradeId" | "teacherId" | "id">);
  };

  const formVars = {
    className: {
      name: "className",
      label: t("Class Name"),
      placeholder: t("Class Name"),
      value: formData?.name,
      autoFocus: true,
    },
    grade: {
      name: "grade",
      label: t("Select Grade"),
      defaultValue: formData?.gradeId,
      valueList: grades,
      getOption: gradeChangeHandler,
    },
    teacher: {
      name: "teacher",
      label: t("Select Teacher"),
      defaultValue: formData?.teacherId,
      valueList: teachers,
      getOption: teacherChangeHandler,
    },
  };

  const editHandler = async () => {
    try {
      let payload = {
        _name: formData?.name,
        _teacherId: formData?.teacherId,
        _id: formData?.id,
      } as unknown as IUpdateClassRequest;
      const data = await updateClass(payload).unwrap();
      if (data?._statusCode === 200) {
        toast({
          label: t("success"),
          message: t("Class Updated"),
          rtl: true,
          type: "success",
          toastId: "classUpdated",
        });
      } else throw data;
    } catch (error) {
      console.log({ error });
    }

    logEvent("ClickOnConfirmEditClass");
    onSubmit();
  };

  const createHandler = async () => {
    try {
      let payload = {
        _name: formData?.name,
        _gradeId: formData?.gradeId,
        _teacherId: formData?.teacherId,
        _schoolId: schoolDetails.schoolId,
      } as unknown as IAddClassRequest;
      const data = await addClass([payload]).unwrap();
      if (data?._statusCode === 200) {
        toast({
          label: t("success"),
          message: t("Class Added"),
          rtl: true,
          type: "success",
          toastId: "classAdded",
        });
      } else throw data;
    } catch (error) {
      console.log({ error });
    }
    onSubmit();
  };
  const changeHandler = (event: {
    currentTarget: {
      name: "className" | "teacherId" | "gradeId";
      value: string;
    };
  }) => {
    const data = { ...formData };
    const { currentTarget: input } = event;
    const errorsObject: any = { ...errors };
    const errorMesssage = validateProperty(input);
    if (errorMesssage) errorsObject[input.name] = errorMesssage;
    else delete errorsObject[input.name];
    setErrors(errorsObject);

    if (input.name === "className") data["name"] = input.value;
    else data[input.name] = input.value as unknown as number;

    setFormData(data as Pick<IClass, "name" | "gradeId" | "teacherId" | "id">);
  };

  const validateProperty = ({
    name,
    value,
  }: {
    name: "className" | "teacherId" | "gradeId";
    value: string;
  }) => {
    const obj = { [name]: value };
    const propertySchema = {
      [name]: ClassSchema[name],
    };
    const { error } = Joi.object(propertySchema).validate(obj);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.object(ClassSchema).validate(
      {
        className: formData?.name,
        gradeId: formData?.gradeId,
        teacherId: formData?.teacherId,
      },
      options
    );
    let errorsObject = { ...errors };
    if (error)
      for (let item of error.details) errorsObject[item.path[0]] = item.message;
    return errorsObject;
  };

  const isFormNotValid = Object.keys(validate()).length > 0;
  return (
    <Typography component="div" sx={customStyles.root}>
      <DeskIcon
        sx={{
          fontSize: "150px",
          position: "absolute",
          top: "-20px",
        }}
      />
      <Typography
        sx={{ color: "rgb(105 123 140)", fontSize: "18px", fontWeight: "bold" }}
      >
        {t("Create class")}
      </Typography>
      <Typography
        component="div"
        sx={customStyles.form}
        style={{ direction: i18n.dir() }}
      >
        {RenderInput({
          ...formVars.className,
          errors: t(errors.className),
          changeHandler,
          styles: { padding: "0 0 30px", width: "100%" },
        })}
        <SelectInput
          {...formVars.grade}
          placeholder={t("Select")}
          style={{ container: { zIndex: 202 } }}
          disabled={actionMode === ActionType.update}
        />
        <SelectInput
          {...formVars.teacher}
          placeholder={t("Select")}
          dropDownHeight="125px"
          searchable
        />
        <Button
          label={actionMode === ActionType.update ? t("Edit") : t("Create")}
          action={
            actionMode === ActionType.update ? editHandler : createHandler
          }
          styles={{ width: "300px", margin: "20px 0" }}
          disable={isFormNotValid || addIsloading || updateIsloading}
        />
      </Typography>
    </Typography>
  );
}

export default Content;
